import type { ReactElement } from 'react';

import { Box, IconButton, TextArea, Tooltip } from '@rango-dev/whale';
import { Check, Copy } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../../hooks/reduxHooks';

import { getProgressData, getSocialProgressText } from './ProfileProgress.helpers';
import styles from './ShareProgress.module.css';

const COPY_ICON_SIZE = 18;
const COPIED_RESET_DELAY_MS = 3000;

export function ShareProgressContent(): ReactElement {
  const profileSummary = useAppSelector((state) => state.profile.profileSummary.private.data);
  const progressData = getProgressData(profileSummary);

  const userLink = `https://app.rango.exchange/profile/${profileSummary?.username}`;
  const [copied, setCopied] = useState<boolean>(false);
  const { t } = useTranslation();

  function copyToClipboard() {
    const socialText = getSocialProgressText(progressData) + userLink;
    if (!copied) {
      void navigator.clipboard.writeText(socialText);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_RESET_DELAY_MS);
    }
  }

  const progressLines = progressData.loading
    ? []
    : progressData.items.filter((item) => item.value).map((item) => `${item.title}: ${item.value}`);

  const shareText = [
    userLink,
    'Visit my profile @RangoExchange',
    '',
    ...progressLines,
    '',
    '#rango #rango_exchange @RangoExchange',
  ].join('\n');

  return (
    <Box position="relative" width="100%">
      <TextArea
        id="profile-share-progress-modal-content"
        className={styles.shareTextArea}
        size="2"
        variant="surface"
        resize="none"
        readOnly
        value={shareText}
        rows={shareText.split('\n').length}
      />
      <Box position="absolute" bottom="4" right="4">
        <Tooltip
          content={copied ? t('Copied to clipboard') : t('Copy to clipboard')}
          sideOffset={8}
          side="bottom"
        >
          <IconButton
            id="profile-share-progress-modal-copy-icon-btn"
            size="3"
            variant="solid"
            color="cyan"
            radius="full"
            onClick={copyToClipboard}
            aria-label={t('Copy to clipboard')}
          >
            {copied ? <Check size={COPY_ICON_SIZE} /> : <Copy size={COPY_ICON_SIZE} />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
